import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X, Filter } from "lucide-react";

export const yearDefault = () => ({
  from: `${new Date().getFullYear()}-01-01`, to: new Date().toISOString().slice(0, 10),
  parties: [], models: [], codes: [], chassis: [], rto: "all",
});
const inputCls = "bg-[#0B0F17] border-[#334155] text-slate-100 h-9";

export const MultiSelect = ({ label, options, selected, onChange, testId }) => {
  const [q, setQ] = useState("");
  const matches = options.filter((o) => !q || o.toLowerCase().includes(q.toLowerCase()));
  const shown = matches.slice(0, 100);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid={testId} variant="outline" size="sm"
          className={`h-9 border-[#334155] bg-[#0B0F17] text-xs ${selected.length ? "text-blue-400 border-blue-500/50" : "text-slate-400"} hover:bg-slate-800 hover:text-white`}>
          {label}{selected.length > 0 && ` (${selected.length})`} <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2 bg-[#111827] border-[#334155] text-slate-100" align="start">
        <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} mb-1.5 h-8 text-xs`} />
        <div className="flex gap-1 mb-1.5">
          <button data-testid={`${testId}-select-all`} onClick={() => onChange(Array.from(new Set([...selected, ...matches])))}
            className="flex-1 text-[10px] py-1 rounded border border-blue-500/40 text-blue-400 hover:bg-blue-500/10">Select all{q ? " (matching)" : ""}</button>
          <button data-testid={`${testId}-deselect-all`} onClick={() => onChange([])}
            className="flex-1 text-[10px] py-1 rounded border border-slate-700 text-slate-400 hover:bg-slate-800">Deselect all</button>
        </div>
        <div className="max-h-56 overflow-auto space-y-0.5">
          {shown.map((o) => (
            <label key={o} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-800 cursor-pointer text-xs">
              <Checkbox checked={selected.includes(o)}
                onCheckedChange={() => onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o])}
                className="border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
              <span className="truncate">{o}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => onChange([])} className="w-full mt-1 h-7 text-xs text-slate-400 hover:text-white">Clear {label}</Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const FilterBar = ({ onApply }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(yearDefault);
  const [opts, setOpts] = useState({ parties: [], models: [], codes: [] });
  const [chq, setChq] = useState("");
  const [chResults, setChResults] = useState([]);

  useEffect(() => { onApply(yearDefault()); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = () => { const d = yearDefault(); setF(d); onApply(d); };
    window.addEventListener("reset-filters", h);
    return () => window.removeEventListener("reset-filters", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/filters/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (chq.trim().length < 3) { setChResults([]); return; }
    const t = setTimeout(() => api.get(`/filters/chassis?q=${encodeURIComponent(chq)}`).then((r) => setChResults(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [chq]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const active = JSON.stringify(f) !== JSON.stringify(yearDefault());

  return (
    <div data-testid="filter-bar" className="bg-[#111827] border border-[#334155] rounded-lg p-2.5 sm:p-3">
      <button data-testid="filter-toggle-mobile" onClick={() => setOpen(!open)}
        className="sm:hidden w-full flex items-center justify-between text-xs text-slate-300 font-medium px-1 py-1">
        <span className="flex items-center gap-2"><Filter className="h-4 w-4 text-slate-500" /> Filters{active ? " (active)" : ""}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`${open ? "flex" : "hidden"} sm:flex flex-wrap items-center gap-2 mt-2 sm:mt-0`}>
      <Filter className="h-4 w-4 text-slate-500 shrink-0 hidden sm:block" />
      <Input data-testid="filter-date-from" type="date" value={f.from} onChange={(e) => set("from", e.target.value)} className={`${inputCls} w-36 text-xs`} title="From date" />
      <span className="text-slate-600 text-xs">→</span>
      <Input data-testid="filter-date-to" type="date" value={f.to} onChange={(e) => set("to", e.target.value)} className={`${inputCls} w-36 text-xs`} title="To date" />
      <MultiSelect testId="filter-party" label="Party" options={opts.parties} selected={f.parties} onChange={(v) => set("parties", v)} />
      <MultiSelect testId="filter-model" label="Model" options={opts.models.map((m) => m.name)} selected={f.models} onChange={(v) => set("models", v)} />
      <MultiSelect testId="filter-code" label="Code" options={opts.codes} selected={f.codes} onChange={(v) => set("codes", v)} />
      <Popover>
        <PopoverTrigger asChild>
          <Button data-testid="filter-chassis" variant="outline" size="sm"
            className={`h-9 border-[#334155] bg-[#0B0F17] text-xs ${f.chassis.length ? "text-blue-400 border-blue-500/50" : "text-slate-400"} hover:bg-slate-800 hover:text-white`}>
            Chassis{f.chassis.length > 0 && ` (${f.chassis.length})`} <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2 bg-[#111827] border-[#334155] text-slate-100" align="start">
          <Input data-testid="filter-chassis-search" placeholder="Type 3+ chars of chassis no…" value={chq} onChange={(e) => setChq(e.target.value)} className={`${inputCls} mb-2 h-8 text-xs font-mono`} />
          <div className="max-h-40 overflow-auto space-y-0.5">
            {chResults.filter((c) => !f.chassis.includes(c)).map((c) => (
              <button key={c} onClick={() => { set("chassis", [...f.chassis, c]); setChq(""); }}
                className="block w-full text-left px-1.5 py-1 rounded hover:bg-slate-800 text-xs font-mono">{c}</button>
            ))}
          </div>
          {f.chassis.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-800">
              {f.chassis.map((c) => (
                <Badge key={c} variant="outline" className="border-blue-500/50 text-blue-400 text-[10px] font-mono gap-1">
                  {c}<button onClick={() => set("chassis", f.chassis.filter((x) => x !== c))}><X className="h-2.5 w-2.5" /></button>
                </Badge>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
      <Select value={f.rto} onValueChange={(v) => set("rto", v)}>
        <SelectTrigger data-testid="filter-rto" className={`${inputCls} w-32 text-xs`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#111827] border-[#334155] text-slate-100">
          <SelectItem value="all">RTO: All</SelectItem>
          <SelectItem value="done">RTO: Done</SelectItem>
          <SelectItem value="pending">RTO: Pending</SelectItem>
        </SelectContent>
      </Select>
      <Button data-testid="filter-apply-btn" size="sm" onClick={() => onApply(f)}
        className="h-9 bg-blue-600 hover:bg-blue-500 text-white text-xs active:scale-95 transition-colors">Apply</Button>
      <Button data-testid="filter-reset-year" size="sm" variant="ghost" onClick={() => { const d = yearDefault(); setF(d); onApply(d); }}
        className={`h-9 text-xs hover:text-white hover:bg-slate-800 ${active ? "text-amber-400" : "text-slate-400"}`}>Reset (this year)</Button>
      </div>
    </div>
  );
};
